import type { AgeRule, LeadSource } from '../types'

export function ageDays(ageMinutes: number): number {
  return Math.floor(ageMinutes / 1440)
}

export function formatDuration(minutes: number): string {
  const abs = Math.abs(Math.round(minutes))
  if (abs < 60) return `${abs}m`
  if (abs < 1440) {
    const h = Math.floor(abs / 60)
    const m = abs % 60
    return m ? `${h}h ${m}m` : `${h}h`
  }
  const d = Math.floor(abs / 1440)
  const h = Math.floor((abs % 1440) / 60)
  return h ? `${d}d ${h}h` : `${d}d`
}

export function formatAge(ageMinutes: number): string {
  if (ageMinutes < 60) return `${Math.round(ageMinutes)} min old`
  if (ageMinutes < 1440) return `${Math.floor(ageMinutes / 60)} hr old`
  const d = Math.floor(ageMinutes / 1440)
  return d === 1 ? '1 day old' : `${d} days old`
}

export function formatRelative(minutesAgo: number): string {
  if (minutesAgo < 1) return 'just now'
  return `${formatDuration(minutesAgo)} ago`
}

export function formatSeconds(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = Math.round(seconds % 60)
  if (m === 0) return `${s}s`
  return `${m}m ${String(s).padStart(2, '0')}s`
}

export function formatPhone(phone: string): string {
  return phone
}

export function requiredCallsFor(source: LeadSource | undefined, ageMinutes: number): number {
  if (!source) return 0
  const days = ageDays(ageMinutes)
  const rule = source.rules.find((r) => r.enabled && days >= r.minDays && days <= r.maxDays)
  return rule ? rule.callsPerDay : 0
}

export function matchingRule(
  source: LeadSource | undefined,
  ageMinutes: number,
): AgeRule | undefined {
  if (!source) return undefined
  const days = ageDays(ageMinutes)
  return source.rules.find((r) => r.enabled && days >= r.minDays && days <= r.maxDays)
}

export function ruleSummary(sourceName: string, rule: AgeRule): string {
  const range =
    rule.minDays === rule.maxDays
      ? `${rule.minDays} day${rule.minDays === 1 ? '' : 's'} old`
      : `${rule.minDays}–${rule.maxDays} days old`
  return `${sourceName} leads that are ${range} remain in the LO queue until ${rule.callsPerDay} call${
    rule.callsPerDay === 1 ? ' has' : 's have'
  } been completed today.`
}

export function findOverlaps(rules: AgeRule[]): Set<string> {
  const overlapping = new Set<string>()
  const active = rules.filter((r) => r.enabled)
  for (let i = 0; i < active.length; i++) {
    for (let j = i + 1; j < active.length; j++) {
      const a = active[i]
      const b = active[j]
      if (a.minDays <= b.maxDays && b.minDays <= a.maxDays) {
        overlapping.add(a.id)
        overlapping.add(b.id)
      }
    }
  }
  return overlapping
}
